import { Component, OnInit, OnDestroy, ChangeDetectorRef, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { Observable, Subject, of } from 'rxjs';
import { startWith, map, finalize, takeUntil } from 'rxjs/operators';
import { Mobile_Apps } from 'app/models/Mobile_Apps';
import { MobileAppsService } from 'app/services/mobile-apps/mobile-apps.service';
import { AwscontrolService } from 'app/services/aws/awscontrol.service';
import { AgenciesService } from 'app/services/agencies/agencies.service';

@Component({
  selector: 'app-ajouter-mobile-apps',
  templateUrl: './ajouter-mobile-apps.component.html',
  styleUrls: ['./ajouter-mobile-apps.component.scss']
})
export class AjouterMobileAppsComponent implements OnInit, OnDestroy {
  @ViewChild('appForm') appForm!: NgForm;

  public agencieAppData: Mobile_Apps;
  public isEditMode = false;
  public isLoading = false;
  public isUploading = false;
  public uploadProgress = 0;
  public imagePreviewUrl: SafeUrl | null = null;
  public uploadedImageUrl: string | null = null;

  // Autocomplete agence
  public agencies: any[] = [];
  public filteredAgencies: Observable<any[]> = of([]);
  public agencySearchControl = '';

  private destroy$ = new Subject<void>();

  constructor(
    private mobileAppService: MobileAppsService,
    private agenciesService: AgenciesService,
    private awsService: AwscontrolService,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private sanitizer: DomSanitizer
  ) {
    this.agencieAppData = new Mobile_Apps();
  }

  ngOnInit(): void {
    this.loadAgencies();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (typeof this.imagePreviewUrl === 'string' && !this.imagePreviewUrl.startsWith('http')) {
      URL.revokeObjectURL(this.imagePreviewUrl);
    }
  }

  loadAgencies(): void {
    this.agenciesService.getList().pipe(takeUntil(this.destroy$)).subscribe({
      next: (data: any) => {
        if (Array.isArray(data)) this.agencies = data;
        else if (data?.data && Array.isArray(data.data)) this.agencies = data.data;
        else if (data?.items && Array.isArray(data.items)) this.agencies = data.items;
        else this.agencies = [];
        this.setupAgencyFilter();
        this.cdr.detectChanges();
      },
      error: () => {
        this.showNotification('Erreur chargement agences', 'error');
        this.agencies = [];
        this.setupAgencyFilter();
      }
    });
  }

  private setupAgencyFilter(): void {
    this.filteredAgencies = of(this.agencySearchControl).pipe(
      startWith(''),
      map(search => this._filterAgencies(search))
    );
  }

  private _filterAgencies(value: string): any[] {
    const filterValue = value?.toLowerCase().trim() || '';
    return this.agencies.filter(agency =>
      agency.name?.toLowerCase().includes(filterValue)
    );
  }

  onAgencySearchChange(searchTerm: string): void {
    this.agencySearchControl = searchTerm;
    this.setupAgencyFilter();
  }

  onAgencySelected(event: MatAutocompleteSelectedEvent): void {
    this.agencieAppData.agencyName = event.option.value.name;
    this.agencySearchControl = event.option.value.name;
    this.setupAgencyFilter();
  }

  displayAgencyFn(agency: any): string {
    return agency?.name ?? '';
  }

  // ==================== IMAGE UPLOAD ====================
  onFileSelected(event: any): void {
    const file = event.target.files[0];
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    const maxSize = 5 * 1024 * 1024;

    if (!validTypes.includes(file.type)) {
      this.showNotification('Format non supporté (JPG, PNG, WEBP)', 'error');
      return;
    }
    if (file.size > maxSize) {
      this.showNotification('L\'image ne doit pas dépasser 5 Mo', 'error');
      return;
    }

    if (typeof this.imagePreviewUrl === 'string' && !this.imagePreviewUrl.startsWith('http')) {
      URL.revokeObjectURL(this.imagePreviewUrl);
    }

    const blobUrl = URL.createObjectURL(file);
    this.imagePreviewUrl = this.sanitizer.bypassSecurityTrustUrl(blobUrl);
    this.cdr.detectChanges();

    this.uploadImage(file);
  }

  private uploadImage(file: File): void {
    this.isUploading = true;
    this.uploadProgress = 0;

    const progressInterval = setInterval(() => {
      if (this.uploadProgress < 90) {
        this.uploadProgress += 10;
        this.cdr.detectChanges();
      }
    }, 200);

    this.awsService.uploadFile(file, 'mobile-apps')
      .pipe(finalize(() => {
        clearInterval(progressInterval);
        this.isUploading = false;
        this.uploadProgress = 100;
        setTimeout(() => { this.uploadProgress = 0; this.cdr.detectChanges(); }, 1000);
      }))
      .subscribe({
        next: (response: any) => {
          const url = response.url || (response.urls && response.urls[0]);
          if (url) {
            this.uploadedImageUrl = url;
            this.agencieAppData.mobile_cover_image_url = url;
            this.showNotification('Image téléchargée avec succès', 'success');
          } else {
            this.showNotification('Erreur : URL non reçue', 'error');
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.showNotification('Erreur lors du téléchargement', 'error');
          this.isUploading = false;
        }
      });
  }

  // ==================== SOUMISSION (CORRIGÉE) ====================
  onSubmit(form: NgForm): void {
    // Validation des champs obligatoires
    if (!this.agencieAppData.name || !this.agencieAppData.agencyName) {
      this.showNotification('Le nom et l’agence sont obligatoires', 'warning');
      return;
    }
    if (!this.uploadedImageUrl && !this.agencieAppData.mobile_cover_image_url) {
      this.showNotification('Veuillez télécharger une image', 'warning');
      return;
    }

    this.isLoading = true;

    const payload = {
      name: this.agencieAppData.name,
      agencyName: this.agencieAppData.agencyName,
      playstore_url: this.agencieAppData.playstore_url || '',
      appstore_url: this.agencieAppData.appstore_url || '',
      mobile_cover_image_url: this.uploadedImageUrl || this.agencieAppData.mobile_cover_image_url
    };

    // ✅ Séparer l'appel pour éviter l'union de types
    if (this.isEditMode && this.agencieAppData.id) {
      // Modification
      this.mobileAppService.updateApp(this.agencieAppData.id, payload)
        .pipe(finalize(() => { this.isLoading = false; this.cdr.detectChanges(); }))
        .subscribe({
          next: (response: string) => {
            this.showNotification('Mise à jour réussie', 'success');
            this.resetForm(form);
          },
          error: () => this.showNotification('Erreur lors de la mise à jour', 'error')
        });
    } else {
      // Création
      this.mobileAppService.createApp(payload)
        .pipe(finalize(() => { this.isLoading = false; this.cdr.detectChanges(); }))
        .subscribe({
          next: (response: string) => {
            this.showNotification('Application créée avec succès', 'success');
            this.resetForm(form);
          },
          error: () => this.showNotification('Erreur lors de la création', 'error')
        });
    }
  }

  resetForm(form: NgForm): void {
    form.resetForm();
    this.agencieAppData = new Mobile_Apps();
    this.isEditMode = false;
    this.uploadedImageUrl = null;
    if (typeof this.imagePreviewUrl === 'string' && !this.imagePreviewUrl.startsWith('http')) {
      URL.revokeObjectURL(this.imagePreviewUrl);
    }
    this.imagePreviewUrl = null;
    this.agencySearchControl = '';
    this.setupAgencyFilter();
    this.cdr.detectChanges();
  }

  private showNotification(message: string, type: 'success' | 'error' | 'warning'): void {
    this.snackBar.open(message, 'Fermer', {
      duration: 3000,
      horizontalPosition: 'end',
      verticalPosition: 'top',
      panelClass: `notification-${type}`
    });
  }
}